/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * WebSocket Error Boundary - Graceful error handling for WebSocket failures
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React from 'react'
import { WifiOff, RefreshCw } from 'lucide-react'
import loggingService from '../services/loggingService'

class WebSocketErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isWebSocketError: false
    }
  }

  static getDerivedStateFromError(error) {
    // Check if this is a WebSocket-related error
    const isWebSocketError =
      error?.message?.toLowerCase().includes('websocket') ||
      error?.message?.toLowerCase().includes('socket.io') ||
      error?.message?.toLowerCase().includes('connection')

    return {
      hasError: true,
      isWebSocketError
    }
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    loggingService.error('WebSocket Error Boundary Caught Error', {
      type: 'websocket_error',
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      isWebSocketError: this.state.isWebSocketError,
      timestamp: new Date().toISOString()
    })

    this.setState({
      error,
      errorInfo
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isWebSocketError: false
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // If it's a WebSocket error, show a more graceful fallback
      if (this.state.isWebSocketError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <WifiOff className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Connection Issue
                  </h3>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We're having trouble with the real-time connection. Don't worry - your progress is
                  saved and the application will automatically fall back to standard HTTP mode.
                </p>
              </div>

              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 mb-4">
                <p className="text-xs text-primary-800 dark:text-primary-300">
                  <strong>Note:</strong> All your answers and progress are being saved via HTTP
                  fallback. Real-time features may be limited, but you can continue working without
                  data loss.
                </p>
              </div>

              {process.env.NODE_ENV === 'development' && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Error Details (Development Only)
                  </h4>
                  <div className="bg-gray-100 dark:bg-gray-700 rounded p-3 max-h-32 overflow-y-auto">
                    <p className="text-xs font-mono text-gray-800 dark:text-gray-200">
                      {this.state.error?.toString()}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={this.handleReset}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Continue with HTTP
                </button>
                <button
                  onClick={this.handleReload}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        )
      }

      // For non-WebSocket errors, fall back to the generic error boundary UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Something went wrong
                </h3>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We're sorry, but something unexpected happened. Please try reloading the page.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={this.handleReload}
                className="flex-1 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleReset}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default WebSocketErrorBoundary
